import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results);
        setOpen(true);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative w-full max-w-sm" ref={ref}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Search jobs, candidates, invoices…"
        className="w-full h-9 rounded-full border border-border bg-[#F8FAFC] pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
        data-testid="global-search-input"
        aria-label="Global search"
      />
      {open && results.length > 0 && (
        <div className="absolute mt-2 w-full min-w-80 bg-white border border-border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="global-search-results">
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => { setOpen(false); setQ(""); navigate(r.link); }}
              className="w-full text-left px-4 py-2.5 hover:bg-[#F8FAFC] border-b border-border/40 last:border-0 flex items-center gap-3"
              data-testid={`search-result-${i}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#2563EB] bg-[#2563EB]/8 rounded-full px-2 py-0.5 shrink-0">
                {r.type}
              </span>
              <span className="min-w-0">
                <p className="text-sm font-medium truncate">{r.title}</p>
                <p className="text-xs text-muted-foreground truncate">{r.sub}</p>
              </span>
            </button>
          ))}
        </div>
      )}
      {open && q.trim().length >= 2 && results.length === 0 && (
        <div className="absolute mt-2 w-full bg-white border border-border rounded-xl shadow-xl z-50 px-4 py-3 text-sm text-muted-foreground" data-testid="search-no-results">
          No results for “{q}”.
        </div>
      )}
    </div>
  );
}
